import { useEffect, useRef } from 'react';

// Listens at the window level for a target keystroke sequence
// (e.g. "turbo"). Calls onTrigger() when the sequence is matched.
//
// Anti-false-trigger rules — without these, common words trigger the
// sequence by accident inside form inputs:
//   · Skip when document.activeElement is an <input>, <textarea>, or
//     a contenteditable element (the user is typing real content,
//     not invoking the easter egg).
//   · Skip when document.hidden = true (the user is in another tab;
//     they might be typing in Slack with Alchemy in the background).
//   · 1.5s between keystrokes resets the buffer (deliberate cadence
//     required, prevents accidental matches across long pauses).
//   · Any non-matching key resets the buffer (TURRO doesn't trigger
//     even though the first two letters match).
//   · Case-insensitive (CAPS LOCK doesn't matter).
//   · Modifier / arrow / function keys ignored (e.key.length !== 1).
//   · While the caller's onTrigger is "running", buffer is locked.
//     Pass `running` as true while you don't want re-fires.
//
// Returns nothing — the hook is purely event-attaching.
export function useKonamiSequence(target, onTrigger, { running = false, timeoutMs = 1500 } = {}) {
  // Refs so the event handler captures up-to-date values without
  // re-attaching the listener on every render (which would lose the
  // in-flight buffer + timer).
  const targetRef = useRef(target);
  const onTriggerRef = useRef(onTrigger);
  const runningRef = useRef(running);
  const timeoutMsRef = useRef(timeoutMs);

  useEffect(() => { targetRef.current = String(target || '').toLowerCase(); }, [target]);
  useEffect(() => { onTriggerRef.current = onTrigger; }, [onTrigger]);
  useEffect(() => { runningRef.current = running; }, [running]);
  useEffect(() => { timeoutMsRef.current = timeoutMs; }, [timeoutMs]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;

    const buffer = [];
    let resetTimer = null;

    const clearReset = () => {
      if (resetTimer) { clearTimeout(resetTimer); resetTimer = null; }
    };

    const isInputFocused = () => {
      const el = document.activeElement;
      if (!el) return false;
      const tag = el.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if (el.isContentEditable) return true;
      return false;
    };

    const handleKeydown = (e) => {
      if (runningRef.current) return;
      if (document.hidden) return;
      if (isInputFocused()) return;
      if (e.key.length !== 1) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      const key = e.key.toLowerCase();
      const target = targetRef.current;
      if (!target) return;

      buffer.push(key);
      // Trim to target length so the rolling window matches
      // suffix-only (so "ssturbo" still triggers if the user mashed
      // an extra s before typing turbo cleanly).
      while (buffer.length > target.length) buffer.shift();

      // Reset on non-matching prefix — strict-prefix check so the
      // buffer doesn't keep stale leading chars that block a real
      // attempt afterwards.
      const bufferStr = buffer.join('');
      if (!target.startsWith(bufferStr)) {
        // Try to recover: if the latest key matches the start of
        // the target, restart the buffer from this single key
        // rather than reset to empty (handles e.g. typing "turtur").
        buffer.length = 0;
        if (target.startsWith(key)) buffer.push(key);
      }

      if (buffer.join('') === target) {
        clearReset();
        buffer.length = 0;
        onTriggerRef.current?.();
        return;
      }

      clearReset();
      resetTimer = setTimeout(() => { buffer.length = 0; }, timeoutMsRef.current);
    };

    window.addEventListener('keydown', handleKeydown);
    return () => {
      window.removeEventListener('keydown', handleKeydown);
      clearReset();
    };
  }, []);
}

export default useKonamiSequence;
